/**
 * The application frame.
 *
 * It owns three things nothing else can own: the column layout, the keyboard
 * shortcuts that cross the whole window, and the details column — which is a
 * host element rather than a component, so whichever view is open decides what
 * belongs in it without the shell having to know about any of them.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useState,
} from 'react'
import type { ChangeEvent, KeyboardEvent as ReactKeyboardEvent, ReactElement } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'

import { useAuth } from '@/api/auth'
import { useConnectionState, useSyncStore } from '@/api/store'
import type { Room } from '@/api/types'
import { IconButton, Modal, SpaceLogo, TextField } from '@/components/ui'
import { DirectRoomLabels, Sidebar, groupRooms, roomLabel, useRoomLabels } from '@/components/Sidebar'
import { ChannelLinkProvider } from '@/lib/channelLinks'
import styles from './AppShell.module.css'

function cx(...values: Array<string | false | null | undefined>): string {
  return values.filter((value): value is string => typeof value === 'string' && value !== '').join(' ')
}

const DETAILS_STORAGE_KEY = 'relaii.details-open'
/*
 * Three sizes, not two.
 *
 * Below `COMPACT_QUERY` — a phone, or a tablet sharing its screen with another
 * app — the navigation is a drawer. Above it the sidebar is always there, which
 * is what design.md asks of an iPad. The details column is a separate question:
 * a sidebar, a conversation and a details pane side by side need real width, so
 * below `WIDE_QUERY` the details pane opens over the conversation instead of
 * beside it, rather than squeezing the reading column to nothing.
 */
const COMPACT_QUERY = '(max-width: 767px)'
const WIDE_QUERY = '(min-width: 1100px)'
const SWITCHER_LIMIT = 40

function readDetailsPreference(): boolean {
  try {
    // Absent means "not yet chosen", and the three-column layout is the default.
    return window.localStorage.getItem(DETAILS_STORAGE_KEY) !== 'false'
  } catch {
    return true
  }
}

function writeDetailsPreference(open: boolean): void {
  try {
    window.localStorage.setItem(DETAILS_STORAGE_KEY, open ? 'true' : 'false')
  } catch {
    // Private browsing refuses to store anything. The preference is not worth
    // failing over; it simply does not survive the reload.
  }
}

function matchesCompact(): boolean {
  return typeof window !== 'undefined' && window.matchMedia(COMPACT_QUERY).matches
}

/** True once there is room for the details column beside the conversation. */
function matchesWide(): boolean {
  return typeof window !== 'undefined' && window.matchMedia(WIDE_QUERY).matches
}

/**
 * The room id the URL is pointing at, without needing a route match.
 *
 * Anybody can post a link, and a link like `/rooms/%E0` is not valid
 * percent-encoding. It leads nowhere, which is an answer — not a reason to
 * take the whole client down during a render.
 */
export function roomIdFromPath(pathname: string): string | null {
  const match = /^\/rooms\/([^/?#]+)/u.exec(pathname)
  const id = match?.[1]
  if (id === undefined) return null
  try {
    return decodeURIComponent(id)
  } catch {
    return null
  }
}

/** True for a key press that a text field or a dialog should keep to itself. */
function keyboardIsElsewhere(target: EventTarget | null, key: 'switcher' | 'rooms'): boolean {
  if (!(target instanceof Element)) return false
  // A dialog is a task in progress; its shortcuts belong to it.
  if (target.closest('[role="dialog"]') !== null) return true
  // Alt+Arrow moves the caret by a word on macOS, and Ctrl+K kills the line
  // in a native field. The composer keeps the room switcher, which is how
  // most people leave it, and gives up only the arrows.
  if (target.closest('input, textarea, select') !== null) return true
  return key === 'rooms' && target.closest('[contenteditable=""], [contenteditable="true"]') !== null
}

// ── Layout context ──────────────────────────────────────────────────────────

export interface AppLayoutValue {
  detailsOpen: boolean
  setDetailsOpen: (open: boolean) => void
  toggleDetails: () => void
  /** Where a view portals the contents of the details column. */
  detailsHost: HTMLElement | null
  /** True once the window is too narrow to keep the navigation on screen. */
  compact: boolean
  /** True while the details pane covers the conversation rather than sitting beside it. */
  detailsOverlay: boolean
  sidebarOpen: boolean
  openSidebar: () => void
  closeSidebar: () => void
  openSwitcher: () => void
}

const AppLayoutContext = createContext<AppLayoutValue | null>(null)

export function useAppLayout(): AppLayoutValue {
  const value = useContext(AppLayoutContext)
  if (value === null) throw new Error('useAppLayout must be used inside AppShell')
  return value
}

// ── Room switcher ───────────────────────────────────────────────────────────

interface RoomSwitcherProps {
  open: boolean
  onClose: () => void
  rooms: readonly Room[]
  labels: ReadonlyMap<string, string>
}

function RoomSwitcher({ open, onClose, rooms, labels }: RoomSwitcherProps): ReactElement {
  const navigate = useNavigate()
  const listId = useId()
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)

  useEffect(() => {
    if (!open) return
    setQuery('')
    setActive(0)
  }, [open])

  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase()
    const found = rooms.filter((room) => {
      if (needle === '') return true
      const haystack = `${roomLabel(room, labels)} ${room.topic ?? ''} ${room.slug ?? ''}`
      return haystack.toLowerCase().includes(needle)
    })
    return found.slice(0, SWITCHER_LIMIT)
  }, [rooms, labels, query])

  const choose = useCallback(
    (room: Room | undefined): void => {
      if (room === undefined) return
      navigate(`/rooms/${room.id}`)
      onClose()
    },
    [navigate, onClose],
  )

  const onKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActive((current) => (matches.length === 0 ? 0 : (current + 1) % matches.length))
      return
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActive((current) =>
        matches.length === 0 ? 0 : (current - 1 + matches.length) % matches.length,
      )
      return
    }
    if (event.key === 'Enter') {
      event.preventDefault()
      choose(matches[active])
    }
  }

  const activeId = matches[active] === undefined ? undefined : `${listId}-${active}`

  return (
    <Modal open={open} onClose={onClose} title="Go to room" size="sm">
      <TextField
        label="Find a room"
        labelHidden
        type="text"
        role="combobox"
        aria-expanded
        aria-controls={listId}
        aria-activedescendant={activeId}
        autoComplete="off"
        placeholder="Type part of a name"
        value={query}
        onChange={(event: ChangeEvent<HTMLInputElement>) => {
          setQuery(event.target.value)
          setActive(0)
        }}
        onKeyDown={onKeyDown}
      />

      {matches.length === 0 ? (
        <p className={styles.switcherEmpty}>Nothing matched.</p>
      ) : (
        <ul className={styles.switcherList} id={listId} role="listbox" aria-label="Rooms">
          {matches.map((room, index) => (
            <li
              key={room.id}
              id={`${listId}-${index}`}
              role="option"
              aria-selected={index === active}
              className={styles.switcherOption}
              onMouseEnter={() => setActive(index)}
              onClick={() => choose(room)}
            >
              <span className={styles.switcherName}>
                {room.kind === 'channel' ? '#' : ''}
                {roomLabel(room, labels)}
              </span>
              {room.topic !== null && room.topic !== '' ? (
                <span className={styles.switcherMeta}>{room.topic}</span>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      <p className={styles.switcherHint}>Arrow keys to move, Enter to open, Escape to close.</p>
    </Modal>
  )
}

// ── Icons ───────────────────────────────────────────────────────────────────

function MenuIcon(): ReactElement {
  return (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true" focusable="false">
      <path d="M2.5 4h11M2.5 8h11M2.5 12h11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
}

// ── Shell ───────────────────────────────────────────────────────────────────

export function AppShell(): ReactElement {
  const navigate = useNavigate()
  const location = useLocation()
  const { rooms } = useSyncStore()
  const { server, user } = useAuth()
  const connection = useConnectionState()
  const labels = useRoomLabels()

  const [compact, setCompact] = useState(matchesCompact)
  const [wide, setWide] = useState(matchesWide)
  const [detailsOpen, setDetailsOpenState] = useState(() => matchesWide() && readDetailsPreference())
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [switcherOpen, setSwitcherOpen] = useState(false)
  const [detailsHost, setDetailsHost] = useState<HTMLElement | null>(null)

  useEffect(() => {
    const media = window.matchMedia(COMPACT_QUERY)
    const onChange = (event: MediaQueryListEvent): void => setCompact(event.matches)
    setCompact(media.matches)
    media.addEventListener('change', onChange)
    return () => media.removeEventListener('change', onChange)
  }, [])

  useEffect(() => {
    const media = window.matchMedia(WIDE_QUERY)
    const onChange = (event: MediaQueryListEvent): void => {
      setWide(event.matches)
      // Anywhere short of the widest layout the details pane lies over the
      // conversation. Opening it by default — or carrying a desktop preference
      // into a narrower window — covers the room after sign-in, and reads as a
      // blank page rather than as a pane the reader asked for.
      setDetailsOpenState(event.matches && readDetailsPreference())
    }
    setWide(media.matches)
    media.addEventListener('change', onChange)
    return () => media.removeEventListener('change', onChange)
  }, [])

  const setDetailsOpen = useCallback((open: boolean): void => {
    setDetailsOpenState(open)
    if (matchesWide()) writeDetailsPreference(open)
  }, [])

  const toggleDetails = useCallback((): void => {
    setDetailsOpenState((current) => {
      const next = !current
      if (matchesWide()) writeDetailsPreference(next)
      return next
    })
  }, [])

  const openSidebar = useCallback(() => setSidebarOpen(true), [])
  const closeSidebar = useCallback(() => setSidebarOpen(false), [])
  const openSwitcher = useCallback(() => setSwitcherOpen(true), [])

  const activeRoomId = roomIdFromPath(location.pathname)

  const ordered = useMemo(() => {
    const { directs, channels } = groupRooms(rooms, labels)
    return [...directs, ...channels]
  }, [rooms, labels])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if ((event.metaKey || event.ctrlKey) && !event.altKey && event.key.toLowerCase() === 'k') {
        if (keyboardIsElsewhere(event.target, 'switcher')) return
        event.preventDefault()
        setSwitcherOpen(true)
        return
      }

      if (
        event.altKey &&
        !event.ctrlKey &&
        !event.metaKey &&
        (event.key === 'ArrowUp' || event.key === 'ArrowDown')
      ) {
        if (ordered.length === 0) return
        if (keyboardIsElsewhere(event.target, 'rooms')) return
        event.preventDefault()

        const step = event.key === 'ArrowDown' ? 1 : -1
        const current = ordered.findIndex((room) => room.id === activeRoomId)
        const next =
          current === -1
            ? step === 1
              ? 0
              : ordered.length - 1
            : (current + step + ordered.length) % ordered.length

        const target = ordered[next]
        if (target !== undefined) navigate(`/rooms/${target.id}`)
        return
      }

      if (event.key === 'Escape') {
        setSidebarOpen(false)
        // A details pane lying over the conversation is dismissed like any
        // other overlay. One sitting in its own column is part of the window,
        // and Escape has nothing to say about it.
        const inDialog =
          event.target instanceof Element && event.target.closest('[role="dialog"]') !== null
        if (!matchesWide() && !inDialog) setDetailsOpenState(false)
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [ordered, activeRoomId, navigate])

  // A slide-over that survives the navigation it just caused would cover the
  // room the user has only this moment chosen.
  useEffect(() => {
    setSidebarOpen(false)
    if (!wide) setDetailsOpenState(false)
  }, [location.pathname, wide])

  const layout = useMemo<AppLayoutValue>(
    () => ({
      detailsOpen,
      setDetailsOpen,
      toggleDetails,
      detailsHost,
      compact,
      detailsOverlay: !wide,
      sidebarOpen,
      openSidebar,
      closeSidebar,
      openSwitcher,
    }),
    [
      detailsOpen,
      setDetailsOpen,
      toggleDetails,
      detailsHost,
      compact,
      wide,
      sidebarOpen,
      openSidebar,
      closeSidebar,
      openSwitcher,
    ],
  )

  const showSidebar = !compact || sidebarOpen
  const workspaceName = server?.display_name ?? server?.server_id ?? 'relaii'

  // Only something the reader has to act on — an expired session, a refused
  // connection. A plain reconnect belongs on the composer, where it is about
  // the message being written rather than about the whole window.
  const connectionNotice = connection.status === 'offline' ? connection.lastError : null

  return (
    <AppLayoutContext.Provider value={layout}>
      {/* `#channel` in a message resolves against the sync store, which only
          this side of the gate has. */}
      <ChannelLinkProvider>
        <div className={styles.shell}>
          {/* Draws nothing. Names the direct rooms, sidebar open or not. */}
          <DirectRoomLabels rooms={rooms} selfId={user?.id ?? null} />

          <a className={styles.skip} href="#relaii-conversation">
            Skip to conversation
          </a>

          {connectionNotice !== null ? (
            <p className={styles.banner} role="status" aria-live="polite">
              {connectionNotice}
            </p>
          ) : null}

          {compact && activeRoomId === null ? (
            <div className={styles.compactBar}>
              <IconButton label="Show navigation" onClick={openSidebar}>
                <MenuIcon />
              </IconButton>
              <SpaceLogo
                src={server?.logo_url ?? null}
                name={workspaceName}
                size="sm"
                decorative
              />
              <span className={styles.compactTitle}>{workspaceName}</span>
            </div>
          ) : null}

          <div className={cx(styles.body, wide && detailsOpen && activeRoomId !== null && styles.withDetails)}>
            {showSidebar ? (
              <div className={styles.nav}>
                <Sidebar onNavigate={closeSidebar} onOpenSwitcher={openSwitcher} />
              </div>
            ) : null}

            {compact && sidebarOpen ? (
              <button
                type="button"
                className={styles.scrim}
                aria-label="Close navigation"
                onClick={closeSidebar}
              />
            ) : null}

            <main className={styles.main} id="relaii-conversation">
              <Outlet />
            </main>

            {!wide && detailsOpen && activeRoomId !== null ? (
              <button
                type="button"
                className={styles.scrim}
                // Not "Close details": the pane carries a button by that name,
                // and two controls announced identically are one too many.
                aria-label="Dismiss details"
                onClick={() => setDetailsOpen(false)}
              />
            ) : null}

            {detailsOpen && activeRoomId !== null ? (
              <aside
                className={cx(styles.details, !wide && styles.detailsOverlay)}
                aria-label="Details"
                ref={setDetailsHost}
              />
            ) : null}
          </div>

          <RoomSwitcher
            open={switcherOpen}
            onClose={() => setSwitcherOpen(false)}
            rooms={ordered}
            labels={labels}
          />
        </div>
      </ChannelLinkProvider>
    </AppLayoutContext.Provider>
  )
}

export default AppShell
